// Types + calls for /api/gateApp/* (GateAppController). Every gate call carries the session's
// gateId; the server re-checks on each request that the user is assigned to that gate (or is a
// gate admin of its location), so nothing here is trusted.

import { apiFetch } from "@/lib/api";

export interface NamedId {
  id: number;
  name: string;
}

export interface GateInfo {
  id: number;
  name: string;
  description?: string | null;
  operationUnit?: NamedId | null;
}

export interface Me {
  gates: GateInfo[];
  isAdmin: boolean;
}

/** An open order (PO) of a store served by the gate — header only, no lines, no rates. */
export interface OrderRow {
  id: number;
  poNumber: string;
  poDate?: string | null;
  vendor?: string | null;
  store: NamedId;
  /** Target date for the FIRST delivery (vendors then deliver in as many consignments as suits them). */
  firstDeliveryBy?: string | null;
  /** First-delivery date passed and nothing has come yet. */
  firstDeliveryLate?: boolean;
  status?: string | null;
  kind?: string | null;
  stage: "NOT_STARTED" | "IN_PROGRESS";
  itemCount: number;
  consignmentCount: number;
  /** Consignments of this order waiting at the gate right now. */
  waitingCount: number;
  lastConsignmentAt?: string | null;
  destinations: { department?: string | null; deliverySites?: string[] }[];
}

export interface OrdersResponse {
  rows: OrderRow[];
  stores: NamedId[];
  truncated: boolean;
}

export interface Destination {
  department?: NamedId | null;
  deliverySites?: string[];
  orders?: { id: number; requestNumber: string }[];
  requesters?: string[];
  neededBy?: string | null;
}

export type ConsignmentStatus = "WAITING" | "RECEIVED" | null;

/** One truck at the gate: consignment N of a purchase order. */
export interface Consignment {
  id: number;
  purchaseOrderId: number;
  poNumber: string;
  vendor?: string | null;
  store?: string | null;
  consignmentNumber?: number | null;
  arrivedAt?: string | null;
  arrivedTime?: string | null;
  vehicleNumber?: string | null;
  driverName?: string | null;
  remarks?: string | null;
  gate?: string | null;
  enteredBy?: string | null;
  challanNumber?: string | null;
  challanDate?: string | null;
  noChallanReason?: string | null;
  challanCollected?: boolean;
  challanHandedOverTo?: string | null;
  challanHandedOverAt?: string | null;
  /** WAITING → RECEIVED; null = recorded before consignment tracking. */
  status?: ConsignmentStatus;
  waitingMinutes?: number | null;
  receivedAt?: string | null;
  receivedGrn?: string | null;
  receivedBy?: string | null;
  remindCount?: number;
  lastRemindedAt?: string | null;
  photoIds: number[];
}

/** An order at the gate: header + its consignments so far (newest first). */
export interface OrderView {
  id: number;
  poNumber: string;
  poDate?: string | null;
  status?: string | null;
  vendor?: { id: number; name: string; contactPerson?: string | null; contactNumber?: string | null } | null;
  store?: NamedId | null;
  expectedDeliveryDate?: string | null;
  itemCount?: number;
  destinations?: Destination[];
  consignments?: Consignment[];
  nextConsignmentNumber?: number;
  /** Why a consignment should not be accepted (closed / service order) — shown as a warning. */
  blocked?: string | null;
  recorded?: Consignment;
  /** Store setting: ON_SCAN = a scan notifies at once; CONFIRM = the guard taps "Notify requester". */
  notifyMode?: "ON_SCAN" | "CONFIRM";
  /** What the scan did (ON_SCAN): the arrival logged + who was told, or the one logged moments ago. */
  arrival?: GateArrival;
  /** ON_SCAN could not log/notify (the guard can still tap "Notify requester"). */
  arrivalError?: string;
  myMessage?: string;
  errorMessage?: string;
}

export interface GateArrival {
  consignment: Consignment;
  /** The same PO was scanned a few minutes ago — nobody was notified twice. */
  alreadyNotified: boolean;
  /** Who was emailed, e.g. "notified 2 recipient(s)". */
  note?: string | null;
}

export interface ConsignmentsResponse {
  rows: Consignment[];
  /** Earlier days' consignments still waiting to be received. */
  waitingEarlier: Consignment[];
  summary: { total: number; waiting: number; received: number };
}

export interface NewConsignment {
  purchaseOrderId: number;
  vehicleNumber?: string;
  driverName?: string;
  challanNumber?: string;
  challanDate?: string;
  noChallanReason?: string;
  challanCollected?: boolean;
  remarks?: string;
  /** data:image/jpeg;base64,… */
  photos?: string[];
}

export interface AdminGate extends GateInfo {
  active: boolean;
  staff: { id: number; userId: number; username: string; name: string }[];
  stores: NamedId[];
}

export interface AdminGatesResponse {
  gates: AdminGate[];
  operationUnits: NamedId[];
}

export interface UserHit {
  id: number;
  username: string;
  name: string;
}

export interface Ok {
  ok?: boolean;
  id?: number;
  errorMessage?: string;
}

function post<T>(action: string, token: string | null, body: Record<string, unknown> = {}, signal?: AbortSignal) {
  return apiFetch<T>(`/api/gateApp/${action}`, { token, method: "POST", body, signal });
}

export const gateApi = {
  me: (token: string | null) => post<Me>("apiMe", token),
  orders: (token: string | null, gateId: number, search: string, signal?: AbortSignal) =>
    post<OrdersResponse>("apiOrders", token, { gateId, search }, signal),
  resolve: (token: string | null, gateId: number, scanned: string) =>
    post<OrderView>("apiResolve", token, { gateId, scanned }),
  order: (token: string | null, gateId: number, purchaseOrderId: number) =>
    post<OrderView>("apiResolve", token, { gateId, purchaseOrderId }),
  decodeQr: (token: string | null, gateId: number, imageBase64: string) =>
    post<{ text?: string; notFound?: boolean }>("apiDecodeQr", token, { gateId, imageBase64 }),
  recordConsignment: (token: string | null, gateId: number, data: NewConsignment) =>
    post<OrderView>("apiRecordConsignment", token, { gateId, ...data }),
  consignments: (token: string | null, gateId: number, q: { day?: string; search?: string }, signal?: AbortSignal) =>
    post<ConsignmentsResponse>("apiConsignments", token, { gateId, ...q }, signal),
  consignment: (token: string | null, gateId: number, id: number) =>
    post<Consignment & { errorMessage?: string }>("apiConsignment", token, { gateId, id }),
  remind: (token: string | null, gateId: number, id: number) =>
    post<Ok & { myMessage?: string }>("apiRemind", token, { gateId, id }),
  handOverChallan: (token: string | null, gateId: number, id: number, to: string) =>
    post<Ok & { myMessage?: string }>("apiHandOverChallan", token, { gateId, id, to }),
  photoPath: (gateId: number, photoId: number) => `/api/gateApp/apiChallanPhoto?gateId=${gateId}&id=${photoId}`,

  adminGates: (token: string | null) => post<AdminGatesResponse>("apiAdminGates", token),
  saveGate: (
    token: string | null,
    data: { id?: number; operationUnitId?: number; name: string; description?: string; active?: boolean },
  ) => post<Ok>("apiAdminSaveGate", token, data),
  deleteGate: (token: string | null, id: number) => post<Ok>("apiAdminDeleteGate", token, { id }),
  searchUsers: (token: string | null, q: string, signal?: AbortSignal) =>
    post<{ users: UserHit[] }>("apiAdminSearchUsers", token, { q }, signal),
  addStaff: (token: string | null, gateId: number, userId: number) =>
    post<Ok>("apiAdminAddStaff", token, { gateId, userId }),
  removeStaff: (token: string | null, id: number) => post<Ok>("apiAdminRemoveStaff", token, { id }),
};

/** yyyy-MM-dd in local time (dates go to the server as strings — Grails Date JSON shifts to UTC). */
export function isoDay(d: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
